// Native bridge: haptics, status bar, splash screen, etc.
// Safely no-ops on web platform.

let _Capacitor = null;
let _Haptics = null;
let _StatusBar = null;
let _SplashScreen = null;
let _ScreenOrientation = null;
let _initialized = false;

// AdMob initialization gate: banner/interstitial/rewarded callers MUST await
// this promise before calling AdMob.showBanner etc. Otherwise the
// BannerExecutor.createNewAdView throws NullPointerException because the
// Capacitor WebView's parent ViewGroup hasn't been attached yet.
let _admobReadyResolve = null;
export const admobReady = new Promise((resolve) => { _admobReadyResolve = resolve; });

async function loadNative() {
  if (_initialized) return;
  _initialized = true;
  try {
    const cap = await import("@capacitor/core");
    _Capacitor = cap.Capacitor;
    if (!_Capacitor?.isNativePlatform?.()) return;
    const [h, s, sp] = await Promise.all([
      import("@capacitor/haptics"),
      import("@capacitor/status-bar"),
      import("@capacitor/splash-screen"),
    ]);
    _Haptics = h;
    _StatusBar = s;
    _SplashScreen = sp;
    // Lock dark status bar
    try {
      await s.StatusBar.setStyle({ style: s.Style.Dark });
      await s.StatusBar.setBackgroundColor({ color: "#050505" });
      await s.StatusBar.setOverlaysWebView({ overlay: false });
    } catch {}
    // Hide splash quickly
    try { await sp.SplashScreen.hide(); } catch {}

    // Initialize AdMob (rewarded ads). UMP/GDPR consent flow runs first so we
    // serve non-personalized ads where appropriate. AdMob.initialize() is the
    // entrypoint per the Capacitor Community plugin docs.
    try {
      const admobMod = await import("@capacitor-community/admob");
      const { AdMob } = admobMod;
      // Step 1: Request consent (UMP). This is a no-op outside the EEA / UK / Brazil
      // unless Google decides otherwise. trackingAuthorizationRequested ensures
      // iOS ATT — harmless on Android.
      try {
        const consent = await AdMob.requestConsentInfo({});
        if (consent?.isConsentFormAvailable && consent.status === "REQUIRED") {
          await AdMob.showConsentForm();
        }
        await AdMob.requestTrackingAuthorization();
      } catch (e) { /* UMP not available on every Android device — fine */ }
      // Step 2: Initialize the SDK.
      // initializeForTesting=true forces TEST ADS for ALL ad units regardless of device.
      // This is safe for: dev builds, internal testing, screenshot capture, etc.
      // Real ads (revenue) only show when REACT_APP_ADMOB_TESTING is unset/false.
      // Toggle via /app/frontend/.env: REACT_APP_ADMOB_TESTING=true|false
      const isTesting = String(process.env.REACT_APP_ADMOB_TESTING || "true").toLowerCase() === "true";
      await AdMob.initialize({
        requestTrackingAuthorization: false, // already done above
        testingDevices: [],
        initializeForTesting: isTesting,
      });
      // Signal that AdMob is ready — banner.js / interstitial.js / rewarded.js
      // await this gate to avoid the BannerExecutor NPE on cold start.
      if (_admobReadyResolve) { _admobReadyResolve(true); _admobReadyResolve = null; }
    } catch (e) {
      // AdMob plugin unavailable — fine, web stub still works
      if (_admobReadyResolve) { _admobReadyResolve(false); _admobReadyResolve = null; }
    }

    // Initialize Google Play Billing (consumable in-app purchases for gems).
    // Lazy-loaded so the web build doesn't ship the cordova-plugin-purchase code.
    try {
      const { initPlayBilling } = await import("./playBilling");
      await initPlayBilling();
    } catch (e) {
      // Plugin not available — web flow continues to use Stripe
    }
  } catch (e) {
    // Plugins not available (web build) — safe no-op
    if (_admobReadyResolve) _admobReadyResolve(false);
  } finally {
    // Final safety: if AdMob block was skipped entirely (e.g. on web), make
    // sure the gate still resolves so awaiters don't hang forever.
    if (_admobReadyResolve) {
      _admobReadyResolve(false);
      _admobReadyResolve = null;
    }
  }
}

// Fire and forget on import
loadNative();

export function isNative() {
  return !!_Capacitor && _Capacitor.isNativePlatform && _Capacitor.isNativePlatform();
}

export async function hapticLight() {
  if (!isNative() || !_Haptics) return;
  try { await _Haptics.Haptics.impact({ style: _Haptics.ImpactStyle.Light }); } catch {}
}

export async function hapticMedium() {
  if (!isNative() || !_Haptics) return;
  try { await _Haptics.Haptics.impact({ style: _Haptics.ImpactStyle.Medium }); } catch {}
}

export async function hapticHeavy() {
  if (!isNative() || !_Haptics) return;
  try { await _Haptics.Haptics.impact({ style: _Haptics.ImpactStyle.Heavy }); } catch {}
}

export async function hapticSuccess() {
  if (!isNative() || !_Haptics) return;
  try { await _Haptics.Haptics.notification({ type: _Haptics.NotificationType.Success }); } catch {}
}

export async function hapticError() {
  if (!isNative() || !_Haptics) return;
  try { await _Haptics.Haptics.notification({ type: _Haptics.NotificationType.Error }); } catch {}
}

// Web fallback using navigator.vibrate (Android browser supports it)
export function vibrate(pattern = 12) {
  if (typeof navigator !== "undefined" && navigator.vibrate) {
    try { navigator.vibrate(pattern); } catch {}
  }
}
